import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  CancelShipmentRequest,
  CreateCompositeShipmentRequest,
  SendFreightDocumentsForReturnShipmentRequest,
} from '~api/som';

import { IgnoredError } from '~src/errors/IgnoredError';
import {
  CarrierContractsApiInstance,
  GentokenApiInstance,
  LabelMergerApiInstance,
  ReportApiInstance,
  SelfcareApiInstance,
  SomApiInstance,
  SomSearchApiInstance,
} from '~src/main/api';
import { useSiteId } from '~src/main/hooks/useSiteId';
import { fetchShippingMethodList } from '~src/main/queries';
import { createContactFromAddress } from '~src/utils/create-contact-from-address';
import { changeFileExtension, extractFileName } from '~src/utils/file';

import { fetchTransportOrderDetails } from '../order/queries';
import { fetchCarrierContract } from '../settings/carrier-contract/queries';
import { fetchShipmentDetails, fetchShipmentMetadata, toShipmentDetails, useShipmentQueryKey } from './queries';
import { ShipmentUpsert } from './types';

function shipmentUpsertToAPIUpsert(shipment: ShipmentUpsert): CreateCompositeShipmentRequest {
  return {
    ...shipment,
    tos_id: shipment.tos?.id,
    address_return: shipment.contact_return?.address,
    address_from: shipment.contact_from?.address,
    address_to: shipment.contact_to?.address,
    customer_info: shipment.customer_info
      ? {
          national_identification_number: shipment.customer_info?.national_identification_number,
          address: shipment.customer_info?.contact?.address,
          email: shipment.customer_info?.contact?.email,
          phone: shipment.customer_info?.contact?.phone,
        }
      : undefined,
    carrier_contract_id: shipment.carrier_contract?.contract_id,
    shipping_method: shipment.shipping_method?.id,
    shipping_date: shipment.shipping_date ? new Date(shipment.shipping_date).toISOString() : undefined,
    delivery_time:
      shipment.delivery_time?.start && shipment.delivery_time.end
        ? {
            start: shipment.delivery_time.start ? new Date(shipment.delivery_time.start).toISOString() : undefined,
            end: shipment.delivery_time.end ? new Date(shipment.delivery_time.end).toISOString() : undefined,
          }
        : undefined,
    direction_type: shipment.direction_type ?? 'OUTBOUND',
  };
}

export function useShipmentHydrateMutation() {
  const siteId = useSiteId();

  return useMutation<ShipmentUpsert, Error, ShipmentUpsert>({
    mutationFn: async payload => {
      try {
        const {
          data: { shipment },
        } = await SomApiInstance.shipmentsHydrate.somHydrateShipment(shipmentUpsertToAPIUpsert(payload), {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const contract = shipment?.carrier_contract_id
          ? await CarrierContractsApiInstance.contracts.carrierContractsGetCarrierContract(shipment.carrier_contract_id)
          : null;

        const methods = shipment?.shipping_method
          ? await SelfcareApiInstance.shippingMethodsList.selfCareListShippingMethods({
              with_custom_booking_methods: true,
              site_id: siteId!,
            })
          : null;

        return {
          ...shipment,
          contact_from: createContactFromAddress('shipment', 'contact_from', shipment?.address_from),
          contact_return: createContactFromAddress('shipment', 'contact_return', shipment?.address_return),
          contact_to: createContactFromAddress('shipment', 'contact_to', shipment?.address_to),
          customer_info: {
            ...shipment?.customer_info,
            contact: createContactFromAddress(
              'shipment',
              'contact_customer_info',
              shipment?.customer_info?.address,
              shipment?.customer_info?.phone,
              shipment?.customer_info?.email
            ),
          },
          shipping_method: methods?.data?.methods?.find(m => m.id === shipment?.shipping_method),
          carrier_contract: contract?.data?.carrier_contract,
          tos: payload.tos,
        };
      } catch (e) {
        if (isAxiosError(e) && e.response?.data.error) {
          throw new Error(e.response.data.error as string);
        }

        throw e;
      }
    },
  });
}

export function useShipmentCreateMutation() {
  const queryKey = useShipmentQueryKey();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (shipment: ShipmentUpsert) => {
      try {
        return SomApiInstance.shipmentsCreate.somCreateCompositeShipment(shipmentUpsertToAPIUpsert(shipment), {
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } catch (e) {
        // don't report validation errors
        if (isAxiosError(e) && e.response?.status === 400) {
          throw new IgnoredError('failed to create shipment due to validation error');
        }

        throw e;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKey.lists() });
    },
  });
}

export function useShipmentUpdateMutation() {
  const queryKey = useShipmentQueryKey();
  const queryClient = useQueryClient();
  const siteId = useSiteId();

  return useMutation({
    mutationFn: async (shipment: ShipmentUpsert) => {
      try {
        const response = await SomApiInstance.shipmentsUpdate.somUpdateShipment(shipmentUpsertToAPIUpsert(shipment));

        await SomApiInstance.customsDeclarationUpsert.somUpsertCustomsDeclaration({
          customs_declaration: shipment.customs_declaration?.general_customs_declaration,
          shipment_id: shipment.shipment_id!,
          site_id: siteId!,
        });

        return response;
      } catch (e) {
        // don't report validation errors
        if (isAxiosError(e) && e.response?.status === 400) {
          throw new IgnoredError(`failed to update shipment #${shipment.shipment_id} due to validation error`);
        }

        throw e;
      }
    },
    onSuccess: ({ data: { shipment } }) => {
      void queryClient.invalidateQueries({ queryKey: queryKey.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKey.detail(shipment?.id) });
    },
  });
}

export function useShipmentBookParcelsMutation() {
  const mutationFn = SomApiInstance.shipmentsBookParcels.somBookParcels;

  return useMutation({
    mutationFn: mutationFn,
  });
}

export function useShipmentBookParcelsBulkMutation() {
  const mutationFn = SomApiInstance.shipmentsBookParcelsBulk.somBookParcelsBulk;

  return useMutation({
    mutationFn: mutationFn,
  });
}

export function useShipmentSchedulePickupMutation() {
  const mutationFn = SomApiInstance.shipmentsSchedulePickup.somSchedulePickup;

  return useMutation({
    mutationFn: mutationFn,
  });
}

export function useShipmentCancelMutation() {
  return useMutation({
    mutationFn: async (payload: CancelShipmentRequest) => {
      try {
        return await SomApiInstance.shipmentsCancel.somCancelShipment(payload);
      } catch (e) {
        // don't report validation errors
        if (isAxiosError(e) && e.response?.status === 501) {
          throw new IgnoredError('failed to cancel shipment due to unsupported carrier');
        }

        throw e;
      }
    },
  });
}

export function useFetchShippingLabelsMutation() {
  const mutationFn = SomSearchApiInstance.shipmentsGetLabels.somSearchFetchShippingLabels;

  return useMutation({
    mutationFn: ({ shipment_ids }: { shipment_ids: string[] }) =>
      mutationFn(
        { convert_labels: true, format: 'PDF', merge_labels: true, shipment_ids },
        {
          // The default array hander seems to be "indexed parameters".
          // But the api expects "repeated parameters".
          paramsSerializer: params => {
            const searchParams = new URLSearchParams();

            for (const [key, value] of Object.entries(params)) {
              if (Array.isArray(value)) {
                value.forEach(v => searchParams.append(key, String(v)));
              } else {
                searchParams.append(key, String(value));
              }
            }

            return searchParams.toString();
          },
        }
      ),
  });
}

export function useGenerateLabelsUrlMutation() {
  const siteId = useSiteId();

  const mutationFn = GentokenApiInstance.labels.genTokenGenerateTemporaryLabelsUrl;

  return useMutation({
    mutationFn: ({ file_name }: { file_name: string }) => mutationFn({ site_id: siteId, file_name }),
  });
}

export function useGenerateReportUrlMutation() {
  const siteId = useSiteId();

  const mutationFn = GentokenApiInstance.blob.genTokenGenerateTemporaryBlobUrl;

  return useMutation({
    mutationFn: ({ file_name }: { file_name: string }) => mutationFn({ file_name, site_id: siteId }),
  });
}

export function useMergeLabelsMutation() {
  const siteId = useSiteId();

  return useMutation({
    mutationFn: async ({ labelUrls }: { labelUrls: string[] }) => {
      const file_names = labelUrls.map(url => changeFileExtension(extractFileName(url), '.pdf'));

      const res = await LabelMergerApiInstance.mergeLabels.labelMergerMergeLabels({ site_id: siteId, file_names });

      return await GentokenApiInstance.labels.genTokenGenerateTemporaryLabelsUrl({
        site_id: siteId,
        file_name: extractFileName(res.data.url!),
      });
    },
  });
}

export function useSendFreightDocumentsMutation() {
  const siteId = useSiteId();

  const mutationFn = SomApiInstance.shipmentsSendFreightDocuments.somSendFreightDocumentsForReturnShipment;

  return useMutation({
    mutationFn: (vars: Omit<SendFreightDocumentsForReturnShipmentRequest, 'site_id'>) =>
      mutationFn({ ...vars, site_id: siteId }),
  });
}

export function useCreateShipmentReportMutation() {
  const mutationFn = ReportApiInstance.shipmentsCreate.reportsCreateShipments;

  return useMutation({
    mutationFn,
  });
}

export function useDetailedShipmentMutation() {
  const siteId = useSiteId();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const shipment = await fetchShipmentDetails({ id });

      const contractId = shipment.shipment?.carrier_contract_id;
      const tosId = shipment.shipment?.tos_id;

      const contract = contractId ? await fetchCarrierContract(contractId) : undefined;
      const metadata = await fetchShipmentMetadata(id);
      const methods = await fetchShippingMethodList(siteId!, { customBookingMethods: true });
      const tos = tosId ? await fetchTransportOrderDetails({ id: tosId }) : undefined;

      return shipmentUpsertToAPIUpsert(toShipmentDetails(shipment, contract, metadata, methods, tos));
    },
  });
}
